import { useState, useEffect } from "react";
import { ArrowRight, MapPin, Clock, Mail } from "lucide-react";
import api from "@/lib/api";
import InquiryForm from "@/components/InquiryForm";

const Baltimore = () => {
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    api.getPrograms({ location: "baltimore-second" }).then(r => setPrograms(r.data)).catch(() => {});
  }, []);

  return (
    <div className="pt-16" data-testid="baltimore-page">
      {/* Hero */}
      <section className="relative py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="https://customer-assets-agu9un31.emergentagent.net/job_squash-nexus/artifacts/y3yxbgr0_image.webp"
            alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#101214]/80"></div>
        </div>
        <div className="container-custom relative z-10">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-4 h-4 text-[#0D6B56]" />
              <span className="overline">Baltimore, Maryland</span>
            </div>
            <h1 className="font-editorial text-4xl sm:text-5xl font-medium text-[#F2EDE4] mb-4">Baltimore</h1>
            <p className="text-sm text-[#C8C0B4] leading-relaxed mb-8 max-w-lg">
              Coppermine Racquet & Fitness, Bare Hills (6 courts) and DS Timonium (3 courts). All programs run at Coppermine.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href="mailto:dreadsports.baltimore@gmail.com?subject=Court%20Booking%20Request%20%E2%80%94%20Baltimore" className="btn-primary text-xs" data-testid="balt-book-court">
                Book a Court by Email <ArrowRight className="w-3.5 h-3.5" />
              </a>
              <a href="https://crf.clubautomation.com" target="_blank" rel="noopener noreferrer" className="btn-secondary text-xs" data-testid="balt-member-login">
                Member Login
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Memberships */}
      <section className="section" style={{ background: "var(--lrg-surface)" }}>
        <div className="container-custom">
          <div className="overline mb-4">Memberships</div>
          <h2 className="font-editorial text-3xl font-medium text-[#F2EDE4] mb-10">Baltimore Membership</h2>
          <div className="grid sm:grid-cols-2 gap-6 max-w-xl">
            <div className="lrg-card text-center py-10">
              <p className="text-[10px] text-[#6B6E71] uppercase tracking-[0.2em] mb-3">Individual</p>
              <p className="font-editorial text-4xl text-[#F2EDE4] mb-1">$85</p>
              <p className="text-[11px] text-[#3A3D40]">per month</p>
            </div>
            <div className="lrg-card text-center py-10">
              <p className="text-[10px] text-[#6B6E71] uppercase tracking-[0.2em] mb-3">Family</p>
              <p className="font-editorial text-4xl text-[#F2EDE4] mb-1">$184</p>
              <p className="text-[11px] text-[#3A3D40]">per month</p>
            </div>
          </div>
        </div>
      </section>

      {/* Facilities */}
      <section className="section" style={{ background: "var(--lrg-bg)" }}>
        <div className="container-custom">
          <div className="overline mb-4">Facilities</div>
          <h2 className="font-editorial text-3xl font-medium text-[#F2EDE4] mb-10">Two Baltimore Venues</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="lrg-card">
              <h3 className="font-display text-lg font-semibold text-[#F2EDE4] mb-2">Coppermine Bare Hills</h3>
              <p className="text-[11px] text-[#6B6E71] mb-3 leading-[1.8]">6 courts. All programs, clinics, and match play run here.</p>
              <div className="flex items-center gap-2 text-[10px] text-[#3A3D40]"><Clock className="w-3 h-3" /> Mon-Sun 3:30PM-9PM</div>
            </div>
            <div className="lrg-card">
              <h3 className="font-display text-lg font-semibold text-[#F2EDE4] mb-2">DS Timonium</h3>
              <p className="text-[11px] text-[#6B6E71] mb-3 leading-[1.8]">3 courts. Unlimited membership — no court time needed. Bring a coach.</p>
              <p className="text-[10px] text-[#3A3D40] mb-2">Court bookings and coaching arranged by email — <a href="mailto:dreadsports.baltimore@gmail.com" className="text-[#0D6B56] hover:text-[#C8F542] transition-colors">dreadsports.baltimore@gmail.com</a></p>
              <div className="flex items-center gap-2 text-[10px] text-[#3A3D40]"><Clock className="w-3 h-3" /> Mon-Sun 3:30PM-9PM</div>
            </div>
          </div>
        </div>
      </section>

      {/* Programs */}
      <section className="section" style={{ background: "var(--lrg-surface)" }}>
        <div className="container-custom">
          <div className="overline mb-4">Programs at Baltimore</div>
          <h2 className="font-editorial text-3xl font-medium text-[#F2EDE4] mb-10">Training Programs</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {programs.map((p, i) => (
              <div key={p.id} className="lrg-card" data-testid={`balt-program-${i}`}>
                <span className="text-[9px] font-medium text-[#0D6B56] tracking-[0.15em] uppercase">{p.category}</span>
                <h3 className="font-display text-base font-semibold text-[#F2EDE4] mt-3 mb-2">{p.name}</h3>
                <p className="text-[11px] text-[#3A3D40] leading-[1.8] mb-3">{p.schedule}</p>
                <p className="text-xs font-medium text-[#0D6B56]">{p.price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="section" style={{ background: "var(--lrg-bg)" }} data-testid="balt-inquiry-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-5 gap-12">
            <div className="lg:col-span-2">
              <div className="overline mb-4">Inquire</div>
              <h2 className="font-editorial text-3xl font-medium text-[#F2EDE4] mb-4">Lessons, Clinics & Match Play</h2>
              <p className="text-xs text-[#6B6E71] leading-[1.9] mb-8">Contact our manager to arrange private lessons, clinics, and match play sessions in Baltimore. Inquiries are answered within 24–48 hours.</p>
              <a href="mailto:dreadsports.baltimore@gmail.com" className="flex items-center gap-2 text-xs text-[#6B6E71] hover:text-[#0D6B56] transition-colors" data-testid="balt-contact-email">
                <Mail className="w-3.5 h-3.5" /> dreadsports.baltimore@gmail.com
              </a>
            </div>
            <div className="lg:col-span-3 lrg-card">
              <InquiryForm location="baltimore" testId="balt-inquiry" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Baltimore;
